import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Slider,
  Checkbox,
  LinearProgress,
  Divider,
  CircularProgress,
} from '@mui/material'
import { Save, Trash2, AlertTriangle, HardDrive } from 'lucide-react'
import SettingsCard from './SettingsCard'
import { toast } from 'react-hot-toast'
import { settingsAPI } from '../services/api'
import { translateBackendKey } from '../utils/translateBackendKey'
import { useAnalytics } from '../hooks/useAnalytics'

interface LogStorage {
  total_size_mb: number
  file_count: number
  oldest_log_date: string | null
  newest_log_date: string | null
  usage_percent: number
  files_by_type: Record<string, number>
  limit_mb: number
  retention_days: number
}

interface SystemSettings {
  log_retention_days: number
  log_save_policy: string
  log_max_total_size_mb: number
  log_cleanup_on_startup: boolean
  cleanup_retention_days: number
}

const LogManagementTab: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { trackSystem, EventAction } = useAnalytics()

  // Local state for form values
  const [logSavePolicy, setLogSavePolicy] = useState('failed_and_warnings')
  const [retentionDays, setRetentionDays] = useState(30)
  const [maxTotalSizeMb, setMaxTotalSizeMb] = useState(500)
  const [cleanupOnStartup, setCleanupOnStartup] = useState(true)
  const [historyRetentionDays, setHistoryRetentionDays] = useState(90)
  const [hasChanges, setHasChanges] = useState(false)

  // Fetch system settings
  const { data: settingsData, isLoading: loadingSettings } = useQuery({
    queryKey: ['system-settings'],
    queryFn: async () => {
      const response = await settingsAPI.getSystemSettings()
      return response.data
    },
  })

  // Fetch log storage stats (refresh every 30s)
  const {
    data: logStorageData,
    isLoading: loadingStorage,
    isError: storageUnavailable,
  } = useQuery({
    queryKey: ['log-storage-stats'],
    queryFn: async () => {
      const response = await settingsAPI.getLogStorageStats()
      return response.data
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  const logStorage: LogStorage | undefined = logStorageData?.storage
  const settings: SystemSettings | undefined = settingsData?.settings

  // Initialize form values from fetched settings
  useEffect(() => {
    if (settings) {
      setLogSavePolicy(settings.log_save_policy || 'failed_and_warnings')
      setRetentionDays(settings.log_retention_days || 30)
      setMaxTotalSizeMb(settings.log_max_total_size_mb || 500)
      setCleanupOnStartup(settings.log_cleanup_on_startup ?? true)
      // Clamp stored values from before the 7-90 scale (or API writes) into range.
      setHistoryRetentionDays(Math.min(90, Math.max(7, settings.cleanup_retention_days ?? 90)))
      setHasChanges(false)
    }
  }, [settings])

  // Track form changes
  useEffect(() => {
    if (settings) {
      const changed =
        logSavePolicy !== (settings.log_save_policy || 'failed_and_warnings') ||
        retentionDays !== (settings.log_retention_days || 30) ||
        maxTotalSizeMb !== (settings.log_max_total_size_mb || 500) ||
        cleanupOnStartup !== (settings.log_cleanup_on_startup ?? true) ||
        historyRetentionDays !== Math.min(90, Math.max(7, settings.cleanup_retention_days ?? 90))
      setHasChanges(changed)
    }
  }, [
    logSavePolicy,
    retentionDays,
    maxTotalSizeMb,
    cleanupOnStartup,
    historyRetentionDays,
    settings,
  ])

  // Save settings mutation
  const saveSettingsMutation = useMutation({
    mutationFn: async () => {
      const response = await settingsAPI.updateSystemSettings({
        log_save_policy: logSavePolicy,
        log_retention_days: retentionDays,
        log_max_total_size_mb: maxTotalSizeMb,
        log_cleanup_on_startup: cleanupOnStartup,
        cleanup_retention_days: historyRetentionDays,
      })
      return response.data
    },
    onSuccess: (data) => {
      toast.success(t('logManagement.savedSuccessfully'))
      if (data.warnings && data.warnings.length > 0) {
        data.warnings.forEach((warning: string) => {
          toast.error(warning, { duration: 6000 })
        })
      }
      queryClient.invalidateQueries({ queryKey: ['system-settings'] })
      queryClient.invalidateQueries({ queryKey: ['log-storage-stats'] })
      setHasChanges(false)
      trackSystem(EventAction.EDIT, {
        section: 'log_management',
        log_save_policy: logSavePolicy,
        retention_days: retentionDays,
        max_total_size_mb: maxTotalSizeMb,
        cleanup_on_startup: cleanupOnStartup,
        cleanup_retention_days: historyRetentionDays,
      })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) || t('logManagement.failedToSaveSettings')
      )
    },
  })

  // Manual cleanup mutation
  const cleanupMutation = useMutation({
    mutationFn: async () => {
      const response = await settingsAPI.manualLogCleanup()
      return response.data
    },
    onSuccess: (data) => {
      toast.success(translateBackendKey(data.message) || t('logManagement.cleanupCompleted'))
      queryClient.invalidateQueries({ queryKey: ['log-storage-stats'] })
      queryClient.invalidateQueries({ queryKey: ['system-settings'] })
      trackSystem(EventAction.DELETE, { section: 'log_management', operation: 'manual_cleanup' })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) || t('logManagement.failedToRunCleanup')
      )
    },
  })

  const handleSaveSettings = () => {
    saveSettingsMutation.mutate()
  }

  const handleCleanup = () => {
    if (window.confirm(t('logManagement.cleanupConfirm'))) {
      cleanupMutation.mutate()
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return t('logManagement.notAvailable')
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  if (loadingSettings) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  const usagePercent = logStorage?.usage_percent || 0
  const isHighUsage = usagePercent >= 80

  return (
    <Box>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.5,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
            }}
          >
            {t('logManagement.title')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('logManagement.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={
            saveSettingsMutation.isPending ? <CircularProgress size={16} /> : <Save size={16} />
          }
          onClick={handleSaveSettings}
          disabled={!hasChanges || saveSettingsMutation.isPending}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {saveSettingsMutation.isPending ? t('logManagement.saving') : t('logManagement.save')}
        </Button>
      </Box>

      <Stack spacing={3}>
        {/* Current Usage Card */}
        <SettingsCard>
          <Stack spacing={3}>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                }}
              >
                <HardDrive size={20} />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {t('logManagement.storageUsage')}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('logManagement.storageUsageDesc')}
              </Typography>
            </Box>

            <Divider />

            {loadingStorage ? (
              <Box
                sx={{
                  py: 2,
                }}
              >
                <LinearProgress />
              </Box>
            ) : !logStorage ? (
              // No figures, whether the route failed or answered without
              // them: the warning alone, no zero figures that would read as
              // an empty store.
              <Alert severity="warning">{t('logManagement.storageUnavailable')}</Alert>
            ) : (
              <>
                {storageUnavailable && (
                  // Stale figures from the last successful fetch: say so
                  // above them.
                  <Alert severity="warning">{t('logManagement.storageUnavailable')}</Alert>
                )}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
                    gap: 3,
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                      }}
                    >
                      {t('logManagement.totalSize')}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      {logStorage?.total_size_mb || 0} MB
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                      }}
                    >
                      {t('logManagement.fileCount')}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      {logStorage?.file_count || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                      }}
                    >
                      {t('logManagement.oldestLog')}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {formatDate(logStorage?.oldest_log_date || null)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                      }}
                    >
                      {t('logManagement.newestLog')}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {formatDate(logStorage?.newest_log_date || null)}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {t('logManagement.usageOfLimit', {
                        percent: usagePercent,
                        limit: logStorage?.limit_mb || 0,
                      })}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {t('logManagement.mbAvailable', {
                        count: Math.max(
                          0,
                          (logStorage?.limit_mb || 0) - (logStorage?.total_size_mb || 0)
                        ),
                      })}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(usagePercent, 100)}
                    color={isHighUsage ? 'warning' : 'primary'}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                {isHighUsage && (
                  <Alert severity="warning" icon={<AlertTriangle size={20} />}>
                    {t('logManagement.highUsageWarning', { percent: usagePercent })}
                  </Alert>
                )}
              </>
            )}
            {/* The cleanup stays reachable whatever the figures say */}
            <Box sx={{ pt: 1 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={
                  cleanupMutation.isPending ? <CircularProgress size={16} /> : <Trash2 size={16} />
                }
                onClick={handleCleanup}
                disabled={cleanupMutation.isPending}
              >
                {cleanupMutation.isPending
                  ? t('logManagement.clearing')
                  : t('logManagement.clearLogs')}
              </Button>
            </Box>
          </Stack>
        </SettingsCard>

        {/* Log Storage Policy */}
        <SettingsCard>
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{
                  fontWeight: 600,
                }}
              >
                {t('logManagement.storagePolicy')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('logManagement.storagePolicyDesc')}
              </Typography>
            </Box>

            <Divider />

            <FormControl component="fieldset">
              <RadioGroup value={logSavePolicy} onChange={(e) => setLogSavePolicy(e.target.value)}>
                <FormControlLabel
                  value="failed_only"
                  control={<Radio />}
                  label={
                    <Box sx={{ py: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {t('logManagement.policyFailedOnly')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {t('logManagement.policyFailedOnlyDesc')}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1, alignItems: 'flex-start', '.MuiRadio-root': { mt: 1.25 } }}
                />
                <FormControlLabel
                  value="failed_and_warnings"
                  control={<Radio />}
                  label={
                    <Box sx={{ py: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {t('logManagement.policyFailedAndWarnings')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {t('logManagement.policyFailedAndWarningsDesc')}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1, alignItems: 'flex-start', '.MuiRadio-root': { mt: 1.25 } }}
                />
                <FormControlLabel
                  value="all_jobs"
                  control={<Radio />}
                  label={
                    <Box sx={{ py: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {t('logManagement.policyAllJobs')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {t('logManagement.policyAllJobsDesc')}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', '.MuiRadio-root': { mt: 1.25 } }}
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </SettingsCard>

        {/* Retention Settings */}
        <SettingsCard>
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{
                  fontWeight: 600,
                }}
              >
                {t('logManagement.retentionSettings')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('logManagement.retentionSettingsDesc')}
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography
                variant="body2"
                gutterBottom
                sx={{
                  fontWeight: 600,
                }}
              >
                {t('logManagement.logRetentionPeriod', { days: retentionDays })}
              </Typography>
              <Box sx={{ px: 1, pt: 1 }}>
                <Slider
                  value={retentionDays}
                  onChange={(_, value) => setRetentionDays(value as number)}
                  min={7}
                  max={90}
                  step={1}
                  marks={[
                    { value: 7, label: '7d' },
                    { value: 30, label: '30d' },
                    { value: 60, label: '60d' },
                    { value: 90, label: '90d' },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('logManagement.logRetentionCaption')}
              </Typography>
            </Box>

            <Box>
              <TextField
                label={t('logManagement.maxTotalSize')}
                type="number"
                value={maxTotalSizeMb}
                onChange={(e) =>
                  setMaxTotalSizeMb(Math.min(10000, Math.max(10, parseInt(e.target.value) || 10)))
                }
                fullWidth
                helperText={t('logManagement.maxTotalSizeHelper')}
                slotProps={{
                  htmlInput: { min: 10, max: 10000, step: 50 },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                gutterBottom
                sx={{
                  fontWeight: 600,
                }}
              >
                {t('logManagement.historyRetentionPeriod', { days: historyRetentionDays })}
              </Typography>
              <Box sx={{ px: 1, pt: 1 }}>
                <Slider
                  value={historyRetentionDays}
                  onChange={(_, value) => setHistoryRetentionDays(value as number)}
                  min={7}
                  max={90}
                  step={1}
                  marks={[
                    { value: 7, label: '7d' },
                    { value: 30, label: '30d' },
                    { value: 60, label: '60d' },
                    { value: 90, label: '90d' },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('logManagement.historyRetentionCaption')}
              </Typography>
            </Box>
          </Stack>
        </SettingsCard>

        {/* Cleanup Options */}
        <SettingsCard>
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{
                  fontWeight: 600,
                }}
              >
                {t('logManagement.automaticCleanup')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('logManagement.automaticCleanupDesc')}
              </Typography>
            </Box>

            <Divider />

            <FormControlLabel
              control={
                <Checkbox
                  checked={cleanupOnStartup}
                  onChange={(e) => setCleanupOnStartup(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {t('logManagement.cleanupOnStartup')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t('logManagement.cleanupOnStartupDesc')}
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </SettingsCard>
      </Stack>
    </Box>
  )
}

export default LogManagementTab
